import type { ReactNode } from 'react'
import { formatCount, formatRange, formatSek } from '../format'
import { InfoTip } from './InfoTip'

// MP-OS-163 — one table Olof can hold against his accounting for the selected range. Every
// figure comes from Heart's one range-total request; nothing is computed here. The cards keep
// their totals (Adnan, 23 Oct): revenue, orders and AOV are repeated here next to the
// components so the table reads on its own. "Net excluding VAT" is left out until Heart says
// how VAT on refunds is treated (docs/business-metrics-net-definition.md).

export type ReconKey = 'gross_revenue' | 'refund_total' | 'discount_total' | 'shipping_revenue' | 'total_tax'
export type ReconStatus = 'verified' | 'missing' | 'invalid' | 'not_reconciled'
export type Reconciliation = Partial<Record<ReconKey, { value: number | null; status: ReconStatus }>>
type Totals = { revenue: number; orders: number; average_order_value: number | null }

const SCOPE = 'Statuses: all except cancelled, failed, pending, draft and not sent. SEK only. Completed days only.'

type Row = { label: string; unit: string; tip: ReactNode } & (
  | { component: ReconKey }
  | { total: keyof Totals }
)

// Quoted text is Heart's own contract wording; "Not stated by Heart" marks what it leaves open.
const ROWS: Row[] = [
  { label: 'Gross revenue', unit: 'SEK', component: 'gross_revenue',
    tip: <>Heart: “gross_revenue excludes them [refunds]”. Not stated by Heart: whether it includes VAT or
      shipping, or is before or after discounts.</> },
  { label: 'Refunds', unit: 'SEK', component: 'refund_total',
    tip: <>Heart: “refund_total is returned positive.” Deducted from gross revenue to give revenue net of
      refunds. Not stated by Heart: the day a refund is counted on, or whether refunded shipping is included.</> },
  { label: 'Revenue net of refunds', unit: 'SEK', total: 'revenue',
    tip: <>Heart: “revenue is NET of refunds; gross_revenue and refund_total are available separately.” Gross
      revenue minus refunds must equal this figure within 0.01 SEK; otherwise both are shown as Not verified. When
      either one is missing or invalid the check cannot run, so the other is Not verified too. Not
      stated by Heart: whether it includes VAT, shipping or discounts.</> },
  { label: 'Discounts', unit: 'SEK', component: 'discount_total',
    tip: <>Heart offers discount_total and lists discount as an accounting component of a total. Not stated by
      Heart: whether revenue is before or after discounts, or the sign.</> },
  { label: 'Shipping', unit: 'SEK', component: 'shipping_revenue',
    tip: <>Heart offers shipping_revenue and lists shipping and refund_shipping as components of a total. Not
      stated by Heart: whether revenue includes shipping, or whether refunded shipping is deducted here.</> },
  { label: 'VAT', unit: 'SEK', component: 'total_tax',
    tip: <>Heart offers total_tax. Not stated by Heart: whether revenue includes VAT, or how VAT on refunds is
      treated, so revenue excluding VAT is not shown.</> },
  { label: 'Orders', unit: 'orders', total: 'orders',
    tip: <>Heart’s orders_count for the whole range in one request, never summed from days or articles. Not
      stated by Heart: that it counts distinct orders, or whether a fully refunded order counts.</> },
  { label: 'Average order value', unit: 'SEK/order', total: 'average_order_value',
    tip: <>Heart’s avg_order_value for the whole range: not an average of daily averages, and not shown when
      there are no orders. Not stated by Heart: that it equals revenue ÷ orders.</> },
]

export function BusinessReconciliation({ total, reconciliation, from, to, partial }: {
  total: Totals
  reconciliation?: Reconciliation
  from: string
  to: string
  partial: boolean
}) {
  // A component the API did not report is not verified: never shown as 0.
  const cell = (row: Row): { text: string; verified: boolean } => {
    if ('component' in row) {
      const entry = reconciliation?.[row.component]
      return entry?.status === 'verified' && entry.value !== null
        ? { text: formatSek(entry.value), verified: true }
        : { text: 'Not verified', verified: false }
    }
    const value = total[row.total]
    if (value === null) return { text: 'Unavailable', verified: true } // AOV with zero orders
    return { text: row.total === 'orders' ? formatCount(value) : formatSek(value), verified: true }
  }
  const cells = ROWS.map((row) => ({ row, ...cell(row) }))
  const unverified = cells.filter((c) => !c.verified).map((c) => c.row.label)
  return (
    <article className="business-card business-reconciliation">
      <h2>Reconciliation</h2>
      <table>
        <caption>
          {`${formatRange(from, to)} · SEK · All suppliers${partial ? ' (partial)' : ''}`}
          <InfoTip label="About the reconciliation table" size="small">
            Every figure is Heart’s own total for the selected range, from one request. {SCOPE}
          </InfoTip>
        </caption>
        <thead><tr><th scope="col">Item</th><th scope="col">Value</th><th scope="col">Unit</th></tr></thead>
        <tbody>
          {cells.map(({ row, text, verified }) => (
            <tr key={row.label} data-verified={verified}>
              <th scope="row">
                {row.label}
                <InfoTip label={`About ${row.label.toLowerCase()}`} size="small">{row.tip} {SCOPE}</InfoTip>
              </th>
              <td>{text}</td>
              <td className="business-unit">{row.unit}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {unverified.length > 0 && (
        <p role="alert">Some figures could not be verified and are not shown: {unverified.join(', ')}.</p>
      )}
    </article>
  )
}
